import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiTrash2, FiMail, FiPhone, FiMapPin, FiCalendar, FiCreditCard, FiAlertTriangle, FiCheckCircle, FiXCircle, FiUser, FiDollarSign, FiClock, FiFileText, FiShield, FiAlertCircle, FiPlus } from 'react-icons/fi';
import { FaCar } from 'react-icons/fa';
import api from '../utils/axios';
import toast from 'react-hot-toast';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [incidentFormData, setIncidentFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    severity: 'Low',
    cost: 0,
    resolved: false
  });

  useEffect(() => {
    fetchClientData();
  }, [id]);

  const fetchClientData = async () => {
    try {
      setLoading(true);

      // Fetch client details
      const clientResponse = await api.get(`/api/clients/${id}`);
      setClient(clientResponse.data.data);

      // Fetch client reservations
      const reservationsResponse = await api.get(`/api/reservations?client=${id}`);
      setReservations(reservationsResponse.data.data);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching client data:', error);
      toast.error('Échec de la récupération des données du client');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/clients/${id}`);
      toast.success('Client supprimé avec succès');
      navigate('/clients');
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Échec de la suppression du client');
    }
  };

  const handleBlacklistToggle = async () => {
    try {
      const endpoint = client.isBlacklisted ? 'unblacklist' : 'blacklist';
      await api.put(`/api/clients/${id}/${endpoint}`);

      // Refresh client data
      const response = await api.get(`/api/clients/${id}`);
      setClient(response.data.data);

      toast.success(`Client ${client.isBlacklisted ? 'retiré de la' : 'ajouté à la'} liste noire avec succès`);
    } catch (error) {
      console.error('Error updating blacklist status:', error);
      toast.error('Échec de la mise à jour du statut de liste noire');
    }
  };

  const handleAddIncident = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/clients/${id}/incidents`, incidentFormData);

      // Refresh client data
      const response = await api.get(`/api/clients/${id}`);
      setClient(response.data.data);

      // Reset form and close modal
      setIncidentFormData({
        date: new Date().toISOString().split('T')[0],
        description: '',
        severity: 'Low',
        cost: 0,
        resolved: false
      });
      setShowIncidentModal(false);

      toast.success('Incident ajouté avec succès');
    } catch (error) {
      console.error('Error adding incident:', error);
      toast.error('Échec de l\'ajout de l\'incident');
    }
  };

  const handleToggleIncidentResolved = async (incidentIndex) => {
    try {
      const updatedIncidents = [...client.incidents];
      updatedIncidents[incidentIndex].resolved = !updatedIncidents[incidentIndex].resolved;

      await api.put(`/api/clients/${id}`, { incidents: updatedIncidents });

      // Refresh client data
      const response = await api.get(`/api/clients/${id}`);
      setClient(response.data.data);

      toast.success(`Incident marqué comme ${updatedIncidents[incidentIndex].resolved ? 'résolu' : 'non résolu'}`);
    } catch (error) {
      console.error('Error updating incident:', error);
      toast.error('Échec de la mise à jour de l\'incident');
    }
  };

  const handleDeleteIncident = async (incidentIndex) => {
    if (window.confirm('Are you sure you want to delete this incident?')) {
      try {
        const updatedIncidents = client.incidents.filter((_, index) => index !== incidentIndex);

        await api.put(`/api/clients/${id}`, { incidents: updatedIncidents });

        // Refresh client data
        const response = await api.get(`/api/clients/${id}`);
        setClient(response.data.data);

        toast.success('Incident supprimé avec succès');
      } catch (error) {
        console.error('Error deleting incident:', error);
        toast.error('Échec de la suppression de l\'incident');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Confirmed': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Completed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      case 'Cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Overdue': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'High': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'Critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <FiUser className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">Client non trouvé</p>
        <Link to="/clients" className="btn btn-primary mt-4">Retour aux Clients</Link>
      </div>
    );
  }

  const totalRentals = reservations.length;
  const totalSpent = reservations.reduce((sum, res) => sum + (res.totalPrice || 0), 0);
  const activeReservations = reservations.filter(res => ['Pending', 'Confirmed', 'Active'].includes(res.status)).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/clients"
            className="group flex items-center text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors"
            title="Retour à la liste des clients"
          >
            <div className="p-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 group-hover:border-primary-200 dark:group-hover:border-primary-800 shadow-sm group-hover:shadow-md transition-all">
              <FiArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            </div>
          </Link>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Détails du Client</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Afficher et gérer les informations du client</p>
          </div>
        </div>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="btn btn-danger flex items-center gap-2 text-sm"
          title="Supprimer le client"
        >
          <FiTrash2 className="w-4 h-4" />
          <span className="hidden sm:inline">Supprimer</span>
          <span className="sm:hidden">Suppr.</span>
        </button>
      </div>

      {/* Client Overview Card */}
      <div className="card p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900 dark:to-primary-800 rounded-full flex items-center justify-center shadow-inner flex-shrink-0">
              <FiUser className="w-7 h-7 sm:w-8 sm:h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{client.name}</h2>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{client.email}</p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2">
                <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <FiPhone className="w-4 h-4 flex-shrink-0" />
                  {client.phone}
                </span>
                <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <FiMapPin className="w-4 h-4 flex-shrink-0" />
                  {client.address?.city}, {client.address?.state}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center lg:items-end gap-2 lg:flex-col">
            {client.isBlacklisted ? (
              <span className="px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border border-red-200 dark:border-red-800">
                Blacklisté
              </span>
            ) : (
              <span className="px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border border-green-200 dark:border-green-800">
                Actif
              </span>
            )}
            <button
              onClick={handleBlacklistToggle}
              className={`text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg font-medium transition-all shadow-sm ${client.isBlacklisted
                ? 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800 border border-green-200'
                : 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-200 dark:hover:bg-red-900/50 border border-red-200 dark:border-red-800'
                }`}
              title={`${client.isBlacklisted ? 'Retirer de la' : 'Ajouter à la'} liste noire`}
            >
              <span className="hidden sm:inline">{client.isBlacklisted ? 'Retirer de la Liste Noire' : 'Ajouter à la Liste Noire'}</span>
              <span className="sm:hidden">{client.isBlacklisted ? 'Retirer' : 'Blacklister'}</span>
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/50 rounded-lg">
                <FaCar className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total des Locations</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{totalRentals}</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
                <FiDollarSign className="w-6 h-6 text-brand-green dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Dépensé</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{totalSpent.toLocaleString()} MAD</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
                <FiClock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Locations Actives</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{activeReservations}</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <FiShield className="w-6 h-6 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Statut du Permis</p>
                <p className={`text-sm font-medium ${client.licenseExpired ? 'text-red-600' :
                  client.licenseExpiringSoon ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                  {client.licenseExpired ? 'Expiré' :
                    client.licenseExpiringSoon ? 'Expire bientôt' : 'Valide'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          <nav className="flex space-x-2 sm:space-x-6 min-w-max">
            {['overview', 'rentals', 'incidents', 'documents'].map((tab) => {
              const tabLabels = {
                overview: 'Aperçu',
                rentals: 'Locations',
                incidents: 'Incidents',
                documents: 'Documents'
              };
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-2 sm:py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${activeTab === tab
                    ? 'border-primary-500 text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                >
                  {tabLabels[tab]}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informations Personnelles</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Date de naissance :</span>
                    <span className="font-medium">{formatDate(client.dateOfBirth)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Âge :</span>
                    <span className="font-medium">{client.age || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">CIN :</span>
                    <span className="font-medium">{client.cin || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Permis de conduire :</span>
                    <span className="font-medium">{client.driverLicenseNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Expiration du permis :</span>
                    <span className={`font-medium ${client.licenseExpired ? 'text-red-600' :
                      client.licenseExpiringSoon ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                      {formatDate(client.licenseExpirationDate)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Adresse</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Rue :</span>
                    <span className="font-medium">{client.address?.street}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Ville :</span>
                    <span className="font-medium">{client.address?.city}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">État/Région :</span>
                    <span className="font-medium">{client.address?.state}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Code postal :</span>
                    <span className="font-medium">{client.address?.zipCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Pays :</span>
                    <span className="font-medium">{client.address?.country}</span>
                  </div>
                </div>
              </div>

              {client.emergencyContact?.name && (
                <div className="md:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Contact d'Urgence</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Nom :</span>
                      <p className="font-medium">{client.emergencyContact.name}</p>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Téléphone :</span>
                      <p className="font-medium">{client.emergencyContact.phone}</p>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Relation :</span>
                      <p className="font-medium">{client.emergencyContact.relationship}</p>
                    </div>
                  </div>
                </div>
              )}

              {client.notes && (
                <div className="md:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notes</h3>
                  <p className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                    {client.notes}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Rentals Tab */}
          {activeTab === 'rentals' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Historique des Locations</h3>
              {reservations.length === 0 ? (
                <div className="text-center py-8">
                  <FaCar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Aucun historique de location trouvé</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reservations.map((reservation) => (
                    <div key={reservation._id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {reservation.car?.brand} {reservation.car?.model}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {reservation.car?.licensePlate}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reservation.status)}`}>
                            {reservation.status}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(reservation.paymentStatus)}`}>
                            {reservation.paymentStatus || 'Unknown'}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Date de début :</span>
                          <p className="font-medium">{formatDate(reservation.startDate)}</p>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Date de fin :</span>
                          <p className="font-medium">{formatDate(reservation.endDate)}</p>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Prix total :</span>
                          <p className="font-medium">{reservation.totalPrice?.toLocaleString() || 'N/A'} MAD</p>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Créé le :</span>
                          <p className="font-medium">{formatDate(reservation.createdAt)}</p>
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <Link
                          to={`/reservations/${reservation._id}`}
                          className="btn btn-secondary text-sm"
                          title="Voir les détails de cette réservation"
                        >
                          Voir les Détails
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Incidents Tab */}
          {activeTab === 'incidents' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Incidents et Violations</h3>
                <button
                  onClick={() => setShowIncidentModal(true)}
                  className="btn bg-brand-green hover:bg-green-700 text-white shadow-md flex items-center gap-2 transition-all"
                  title="Ajouter un nouvel incident ou violation"
                >
                  <FiPlus className="w-4 h-4" />
                  Ajouter un Incident
                </button>
              </div>

              {(!client.incidents || client.incidents.length === 0) ? (
                <div className="text-center py-8">
                  <FiCheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Aucun incident enregistré</p>
                  <button
                    onClick={() => setShowIncidentModal(true)}
                    className="btn bg-brand-green hover:bg-green-700 text-white shadow-md transition-all mt-4"
                    title="Ajouter le premier incident pour ce client"
                  >
                    Ajouter le Premier Incident
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {client.incidents.map((incident, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {incident.description}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {formatDate(incident.date)}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(incident.severity)}`}>
                            {incident.severity}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${incident.resolved
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                            {incident.resolved ? 'Résolu' : 'Ouvert'}
                          </span>
                        </div>
                      </div>

                      {incident.cost > 0 && (
                        <div className="text-sm mb-3">
                          <span className="text-gray-600 dark:text-gray-400">Coût :</span>
                          <span className="font-medium text-red-600 ml-2">{incident.cost.toLocaleString()} MAD</span>
                        </div>
                      )}

                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => handleToggleIncidentResolved(index)}
                          className={`btn btn-sm ${incident.resolved
                            ? 'btn-warning'
                            : 'btn-success'
                            }`}
                          title={`Marquer l'incident comme ${incident.resolved ? 'non résolu' : 'résolu'}`}
                        >
                          {incident.resolved ? 'Marquer comme Non Résolu' : 'Marquer comme Résolu'}
                        </button>
                        <button
                          onClick={() => handleDeleteIncident(index)}
                          className="btn btn-danger btn-sm"
                          title="Supprimer cet incident"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Documents et Conformité</h3>
              <div className="space-y-4">
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FiFileText className="w-6 h-6 text-blue-600" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Permis de Conduire</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Numéro : {client.driverLicenseNumber}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Expire le</p>
                      <p className={`font-medium ${client.licenseExpired ? 'text-red-600' :
                        client.licenseExpiringSoon ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                        {formatDate(client.licenseExpirationDate)}
                      </p>
                    </div>
                  </div>
                </div>

                {client.isBlacklisted && (
                  <div className="border border-red-200 dark:border-red-700 rounded-lg p-4 bg-red-50 dark:bg-red-900/20">
                    <div className="flex items-center gap-3">
                      <FiAlertTriangle className="w-6 h-6 text-red-600" />
                      <div>
                        <h4 className="font-medium text-red-900 dark:text-red-100">Statut de Liste Noire</h4>
                        <p className="text-sm text-red-700 dark:text-red-300">
                          {client.blacklistReason || 'Aucune raison spécifiée'}
                        </p>
                        {client.blacklistDate && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            Ajouté à la liste noire le : {formatDate(client.blacklistDate)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <FiUser className="w-6 h-6 text-green-600" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Statut du Compte</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {client.isActive ? 'Actif' : 'Inactif'} • Créé le {formatDate(client.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <FiAlertCircle className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Supprimer le Client</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Êtes-vous sûr de vouloir supprimer {client.name} ? Cette action ne peut pas être annulée et supprimera toutes les données associées.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger"
              >
                Supprimer le Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Incident Modal */}
      {showIncidentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <FiAlertTriangle className="w-6 h-6 text-orange-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ajouter un Nouvel Incident</h3>
            </div>

            <form onSubmit={handleAddIncident} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  required
                  value={incidentFormData.date}
                  onChange={(e) => setIncidentFormData({ ...incidentFormData, date: e.target.value })}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  required
                  rows={3}
                  value={incidentFormData.description}
                  onChange={(e) => setIncidentFormData({ ...incidentFormData, description: e.target.value })}
                  placeholder="Décrivez l'incident ou la violation..."
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Gravité
                </label>
                <select
                  value={incidentFormData.severity}
                  onChange={(e) => setIncidentFormData({ ...incidentFormData, severity: e.target.value })}
                  className="input"
                >
                  <option value="Low">Faible</option>
                  <option value="Medium">Moyenne</option>
                  <option value="High">Élevée</option>
                  <option value="Critical">Critique</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Coût (MAD)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={incidentFormData.cost}
                  onChange={(e) => setIncidentFormData({ ...incidentFormData, cost: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  className="input"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="resolved"
                  checked={incidentFormData.resolved}
                  onChange={(e) => setIncidentFormData({ ...incidentFormData, resolved: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="resolved" className="text-sm text-gray-700 dark:text-gray-300">
                  Marquer comme résolu
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowIncidentModal(false)}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  Ajouter l'Incident
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetail; 